import { useEffect, useState } from "react";
import { fetchVmetaDatasetFiles } from "@via/pages/projects/runs/runs.api.tsx";
import { isVmetaCollection } from "@via/pages/projects/runs/overview/inputs/input-file/single-file-spreadsheet";
import type { SaveRunInputs, VmetaDataSetFileResponse } from "common";

export type VmetaFilesMapValue = VmetaDataSetFileResponse & {
  datasetName: string;
};

export type VmetaFilesMap = Record<string, VmetaFilesMapValue>;

export const useVmetaDatasetFiles = (paramsInputs?: SaveRunInputs[]) => {
  const [vmetaFilesByCollectionId, setVmetaFilesByCollectionId] =
    useState<VmetaFilesMap>({});
  const [isLoadingVmeta, setIsLoadingVmeta] = useState(false);

  useEffect(() => {
    if (!Array.isArray(paramsInputs) || paramsInputs.length === 0) {
      setVmetaFilesByCollectionId({});
      return;
    }

    const vmetaInputs = paramsInputs.filter(
      input => isVmetaCollection(input) && input.vmetaCollectionId
    );

    if (vmetaInputs.length === 0) {
      setVmetaFilesByCollectionId({});
      return;
    }

    let cancelled = false;

    const fetchAll = async () => {
      try {
        setIsLoadingVmeta(true);

        const entries: [string, VmetaFilesMapValue][] = [];
        for (const vmetaInput of vmetaInputs) {
          const datasetName = vmetaInput.value || vmetaInput.name;
          if (!isVmetaCollection(vmetaInput) || !vmetaInput.vmetaCollectionId)
            continue;

          try {
            const { data, meta } = await fetchVmetaDatasetFiles(
              vmetaInput.vmetaCollectionId
            );
            if (Array.isArray(data)) {
              entries.push([
                vmetaInput.vmetaCollectionId,
                { data, meta, datasetName },
              ]);
            }
          } catch (err) {
            console.error(
              "Failed to fetch vmeta dataset files for",
              vmetaInput.vmetaCollectionId,
              err
            );
          }
        }

        if (!cancelled) {
          setVmetaFilesByCollectionId(Object.fromEntries(entries));
        }
      } finally {
        if (!cancelled) {
          setIsLoadingVmeta(false);
        }
      }
    };

    void fetchAll();

    return () => {
      cancelled = true;
    };
  }, [paramsInputs]);

  return { vmetaFilesByCollectionId, isLoadingVmeta };
};
